/**
 * SIM-1502 — top-level page for the Dottie context editor. Per-tenant sections live in URL-synced
 * tabs (`?tab=`, tenant in `?tenantId=`) and the header's missing-context chips are clickable,
 * jumping to the tab listing the unmapped rows (SIM-1678, SIM-1679). The page owns its own
 * `QueryClientProvider`, so the rest of web-admin, which has none, is unaffected.
 */
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from "@simplicity/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { AdditionalContextSection } from "./AdditionalContextSection";
import { FieldDefinitionsSection } from "./FieldDefinitionsSection";
import { buildSchemaIndex, missingContextCounts } from "./schemaConnection";
import { SkillsSection } from "./SkillsSection";
import { TableDefinitionsSection } from "./TableDefinitionsSection";
import { TenantPicker } from "./TenantPicker";
import type { WarehouseSchema } from "./types";
import { UniversalPanel } from "./UniversalPanel";
import { useFieldDefs, useTableDefs, useTenants, useWarehouseSchema } from "./useDottieContext";
import { WarehouseSchemaDrawer } from "./WarehouseSchemaDrawer";

type TabValue = "skills" | "tables" | "fields" | "additional";
const TAB_VALUES: readonly TabValue[] = ["skills", "tables", "fields", "additional"];
const DEFAULT_TAB: TabValue = "skills";
function isTabValue(value: string | null): value is TabValue {
  return value !== null && (TAB_VALUES as readonly string[]).includes(value);
}

const EMPTY_SCHEMA: WarehouseSchema = {
  fetchedAt: "",
  tenantId: "",
  error: null,
  datasets: [],
};

function Chip({
  tone,
  onClick,
  ariaLabel,
  children,
}: {
  tone: "warning" | "info";
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  const className =
    tone === "warning"
      ? "inline-flex items-center gap-1 rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      : "inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <button type="button" className={className} onClick={onClick} aria-label={ariaLabel}>
      {children}
    </button>
  );
}

function MissingContextChips({
  tenantId,
  onJumpToTab,
}: {
  tenantId: string;
  onJumpToTab: (tab: TabValue) => void;
}) {
  const tableDefsQuery = useTableDefs(tenantId);
  const fieldDefsQuery = useFieldDefs(tenantId);
  const warehouseQuery = useWarehouseSchema(tenantId);

  const counts = useMemo(() => {
    const index = buildSchemaIndex(warehouseQuery.data ?? EMPTY_SCHEMA);
    return missingContextCounts(tableDefsQuery.data ?? [], fieldDefsQuery.data ?? [], index);
  }, [warehouseQuery.data, tableDefsQuery.data, fieldDefsQuery.data]);

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="missing-context-chips"
      aria-label="Missing context summary"
    >
      <Chip
        tone="warning"
        onClick={() => onJumpToTab("tables")}
        ariaLabel={`Jump to Tables tab — ${counts.orphanTables} tables not in warehouse`}
      >
        {counts.orphanTables} tables not in warehouse
      </Chip>
      <Chip
        tone="warning"
        onClick={() => onJumpToTab("fields")}
        ariaLabel={`Jump to Fields tab — ${counts.orphanFields} fields not in warehouse`}
      >
        {counts.orphanFields} fields not in warehouse
      </Chip>
      <Chip
        tone="info"
        onClick={() => onJumpToTab("tables")}
        ariaLabel={`Jump to Tables tab — ${counts.unmappedTables} warehouse tables without a definition`}
      >
        {counts.unmappedTables} warehouse tables without a definition
      </Chip>
      <Chip
        tone="info"
        onClick={() => onJumpToTab("fields")}
        ariaLabel={`Jump to Fields tab — ${counts.unmappedColumns} warehouse columns without a definition`}
      >
        {counts.unmappedColumns} warehouse columns without a definition
      </Chip>
    </div>
  );
}

function PageBody() {
  const tenantsQuery = useTenants();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const urlTenantId = searchParams.get("tenantId");
  const urlTab = searchParams.get("tab");
  const activeTab: TabValue = isTabValue(urlTab) ? urlTab : DEFAULT_TAB;

  const resolvedTenantId = urlTenantId ?? tenantsQuery.data?.[0]?.id ?? null;

  const handleChange = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tenantId", id);
    setSearchParams(next, { replace: true });
  };

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-10">
      <header className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold text-foreground">Dottie context</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(true)}
            disabled={!resolvedTenantId}
          >
            Show warehouse schema
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Universal defaults are at the top — expand to view the system prompt and edit universal
          skills / field definitions. Per-tenant editing is below; click a chip to jump to the
          relevant tab.
        </p>
        {resolvedTenantId && (
          <MissingContextChips tenantId={resolvedTenantId} onJumpToTab={handleTabChange} />
        )}
      </header>

      <UniversalPanel />

      <TenantPicker selectedTenantId={resolvedTenantId} onChange={handleChange} />

      {resolvedTenantId ? (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="skills">Skills</TabsTrigger>
            <TabsTrigger value="tables">Tables</TabsTrigger>
            <TabsTrigger value="fields">Fields</TabsTrigger>
            <TabsTrigger value="additional">Additional context</TabsTrigger>
          </TabsList>
          <TabsContent value="skills" className="mt-6">
            <SkillsSection tenantId={resolvedTenantId} />
          </TabsContent>
          <TabsContent value="tables" className="mt-6">
            <TableDefinitionsSection tenantId={resolvedTenantId} />
          </TabsContent>
          <TabsContent value="fields" className="mt-6">
            <FieldDefinitionsSection tenantId={resolvedTenantId} />
          </TabsContent>
          <TabsContent value="additional" className="mt-6">
            <AdditionalContextSection tenantId={resolvedTenantId} />
          </TabsContent>
        </Tabs>
      ) : (
        <p className="text-sm text-muted-foreground">Select a tenant to begin editing.</p>
      )}

      <WarehouseSchemaDrawer
        tenantId={resolvedTenantId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}

export function DottieContextPage() {
  // One client per mount; the page is the only consumer in web-admin today.
  const client = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } }),
    [],
  );
  return (
    <QueryClientProvider client={client}>
      <PageBody />
    </QueryClientProvider>
  );
}
